import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import './platform-toast.css';

const ToastContext = createContext(null);

const DEFAULT_TOAST_DURATION_MS = 5000;

let nextToastId = 0;

function PlatformToastItem({ id, message, variant, duration, onDismiss }) {
  useEffect(() => {
    if (duration <= 0) return undefined;

    const timer = window.setTimeout(() => onDismiss(id), duration);
    return () => window.clearTimeout(timer);
  }, [id, duration, onDismiss]);

  const Icon = variant === 'danger'
    ? FaExclamationCircle
    : variant === 'deleted'
      ? FaTrashAlt
      : variant === 'info'
        ? FaInfoCircle
        : FaCheckCircle;

  return (
    <div
      className={`platform-toast platform-toast--${variant}`}
      role="alert"
      aria-live="polite"
    >
      <div className="platform-toast__body">
        <Icon className="platform-toast__icon" aria-hidden="true" />
        <p className="platform-toast__message">{message}</p>
        <button
          type="button"
          className="platform-toast__close"
          onClick={() => onDismiss(id)}
          aria-label="Zapri obvestilo"
        >
          <FaTimes aria-hidden="true" />
        </button>
      </div>
      {duration > 0 && (
        <div
          className="platform-toast__progress"
          style={{ animationDuration: `${duration}ms` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((
    message,
    variant = 'success',
    duration = DEFAULT_TOAST_DURATION_MS,
  ) => {
    const id = ++nextToastId;
    setToasts((prev) => [...prev, { id, message, variant, duration }]);
  }, []);

  const toast = useMemo(() => ({
    success: (message, duration) => push(message, 'success', duration),
    deleted: (message, duration) => push(message, 'deleted', duration),
    error: (message, duration) => push(message, 'danger', duration),
    info: (message, duration) => push(message, 'info', duration),
  }), [push]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="platform-toast-container" aria-live="polite" aria-relevant="additions">
        {toasts.map((item) => (
          <PlatformToastItem
            key={item.id}
            id={item.id}
            message={item.message}
            variant={item.variant}
            duration={item.duration}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
